export type DateParts = {
  day: string;
  month: string;
  year: string;
};

type RequestedArchiveDate = {
  isoDate: string;
  label: string;
  requestedAt: number;
  waybackTimestamp: string;
};

export type WaybackSnapshot = {
  candidate: string;
  digest?: string;
  mimetype?: string;
  original?: string;
  replayUrl: string;
  statuscode?: string;
  timestamp: string;
};

export type WaybackSearchResult = {
  alternates?: WaybackSnapshot[];
  candidate?: string;
  candidates?: string[];
  requestedDate?: string;
  requestedDateLabel?: string;
  searchedDomain?: string;
  snapshot?: WaybackSnapshot | null;
};

type WaybackCdxDirectionResult = {
  candidate?: string;
  direction?: 'before' | 'after';
  snapshots?: WaybackSnapshot[];
};

export type WaybackSearchProgressStep =
  | 'prepare'
  | 'availability'
  | 'availability-hit'
  | 'cdx-before'
  | 'cdx-after'
  | 'cdx-hit'
  | 'best-effort'
  | 'complete';

export type WaybackSearchProgress = {
  candidate?: string;
  detail: string;
  endpoint?: string;
  step: WaybackSearchProgressStep;
};

export type WaybackSearchProgressHandler = (progress: WaybackSearchProgress) => void;

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export class InvalidArchiveDateError extends Error {
  constructor(message = 'Pick a valid calendar date.') {
    super(message);
    this.name = 'InvalidArchiveDateError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export class WaybackServiceError extends Error {
  constructor(message = 'Wayback Machine is not responding right now. Please try again in a moment.') {
    super(message);
    this.name = 'WaybackServiceError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export function normalizeDomain(value: string): string {
  return value
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^\/+/, '')
    .replace(/\/.*$/, '')
    .replace(/:\d+$/, '')
    .toLowerCase();
}

export function getDaysInMonth(year: number, month: number): number {
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return 31;
  }

  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function formatArchiveDateLabel({ day, month, year }: DateParts): string {
  const monthIndex = Number(month) - 1;
  const monthLabel = MONTH_LABELS[monthIndex] ?? month;

  return `${monthLabel} ${Number(day)}, ${year}`;
}

export function buildRequestedArchiveDate(parts: DateParts): RequestedArchiveDate | null {
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }

  const maxDay = getDaysInMonth(year, month);

  if (month < 1 || month > 12 || day < 1 || day > maxDay) {
    return null;
  }

  return {
    isoDate: `${parts.year}-${parts.month}-${parts.day}`,
    label: formatArchiveDateLabel(parts),
    requestedAt: Date.UTC(year, month - 1, day, 12, 0, 0),
    waybackTimestamp: `${parts.year}${parts.month}${parts.day}120000`,
  };
}

export function buildDomainCandidates(domain: string): string[] {
  const normalized = normalizeDomain(domain);

  if (!normalized) {
    return [];
  }

  const candidates = normalized.startsWith('www.')
    ? [normalized, normalized.slice(4)]
    : [normalized, `www.${normalized}`];

  return Array.from(new Set(candidates.filter(Boolean)));
}

async function readJsonSafely(response: Response): Promise<Record<string, unknown> | null> {
  try {
    return await response.json() as Record<string, unknown>;
  } catch (_) {
    return null;
  }
}

function buildSearchResult(payload: WaybackSearchResult | null): WaybackSearchResult {
  return {
    alternates: Array.isArray(payload?.alternates) ? payload?.alternates : [],
    candidate: payload?.candidate,
    candidates: Array.isArray(payload?.candidates) ? payload?.candidates : [],
    requestedDate: payload?.requestedDate,
    requestedDateLabel: payload?.requestedDateLabel,
    searchedDomain: payload?.searchedDomain,
    snapshot: payload?.snapshot ?? null,
  };
}

function parseWaybackTimestamp(value: string | undefined): number {
  const match = String(value || '').match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/);

  if (!match) {
    return Number.POSITIVE_INFINITY;
  }

  const [, year, month, day, hour, minute, second] = match;

  return Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  );
}

function dedupeSnapshots(snapshots: WaybackSnapshot[]): WaybackSnapshot[] {
  const seen = new Set<string>();

  return snapshots.filter((snapshot) => {
    const key = `${snapshot.timestamp} ${snapshot.original || ''}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function rankSnapshots(
  snapshots: WaybackSnapshot[],
  requestedDate: RequestedArchiveDate,
  candidates: string[],
): WaybackSnapshot[] {
  const candidateOrder = new Map(candidates.map((candidate, index) => [candidate, index]));

  return [...snapshots].sort((left, right) => {
    const leftDistance = Math.abs(parseWaybackTimestamp(left.timestamp) - requestedDate.requestedAt);
    const rightDistance = Math.abs(parseWaybackTimestamp(right.timestamp) - requestedDate.requestedAt);

    if (leftDistance !== rightDistance) {
      return leftDistance - rightDistance;
    }

    const leftCandidate = candidateOrder.get(left.candidate) ?? Number.POSITIVE_INFINITY;
    const rightCandidate = candidateOrder.get(right.candidate) ?? Number.POSITIVE_INFINITY;

    if (leftCandidate !== rightCandidate) {
      return leftCandidate - rightCandidate;
    }

    return left.timestamp.localeCompare(right.timestamp);
  });
}

function buildReplayUrl(timestamp: string, original: string): string {
  const replayTarget = /^https?:\/\//i.test(original) ? original : `http://${original}`;

  return `https://web.archive.org/web/${timestamp}if_/${replayTarget}`;
}

function buildBestEffortSnapshot(candidate: string, requestedDate: RequestedArchiveDate): WaybackSnapshot {
  const original = `http://${candidate}/`;

  return {
    candidate,
    original,
    replayUrl: buildReplayUrl(requestedDate.waybackTimestamp, original),
    timestamp: requestedDate.waybackTimestamp,
  };
}

async function fetchWaybackStep(
  endpoint: string,
  params: URLSearchParams,
  fetchImpl: typeof fetch,
): Promise<WaybackSearchResult> {
  const response = await fetchImpl(`${endpoint}?${params.toString()}`, {
    headers: {
      accept: 'application/json',
    },
  });

  const payload = await readJsonSafely(response);
  const message = typeof payload?.message === 'string' ? payload.message : undefined;
  const errorCode = typeof payload?.error === 'string' ? payload.error : undefined;

  if (!response.ok) {
    if (errorCode === 'InvalidArchiveDate') {
      throw new InvalidArchiveDateError(message);
    }

    throw new WaybackServiceError(message);
  }

  return buildSearchResult(payload as WaybackSearchResult | null);
}

async function fetchCdxDirection(
  params: URLSearchParams,
  fetchImpl: typeof fetch,
): Promise<WaybackCdxDirectionResult> {
  const response = await fetchImpl(`/api/wayback/cdx-direction?${params.toString()}`, {
    headers: {
      accept: 'application/json',
    },
  });

  const payload = await readJsonSafely(response);
  const message = typeof payload?.message === 'string' ? payload.message : undefined;
  const errorCode = typeof payload?.error === 'string' ? payload.error : undefined;

  if (!response.ok) {
    if (errorCode === 'InvalidArchiveDate') {
      throw new InvalidArchiveDateError(message);
    }

    throw new WaybackServiceError(message);
  }

  return {
    candidate: typeof payload?.candidate === 'string' ? payload.candidate : undefined,
    direction: payload?.direction === 'after' ? 'after' : 'before',
    snapshots: Array.isArray(payload?.snapshots) ? payload?.snapshots as WaybackSnapshot[] : [],
  };
}

export async function searchWaybackForDate(
  domain: string,
  dateParts: DateParts,
  fetchImpl: typeof fetch = fetch,
  onProgress?: WaybackSearchProgressHandler,
): Promise<WaybackSearchResult> {
  const requestedDate = buildRequestedArchiveDate(dateParts);

  if (!requestedDate) {
    throw new InvalidArchiveDateError();
  }

  const normalizedDomain = normalizeDomain(domain);

  if (!normalizedDomain) {
    return buildSearchResult(null);
  }

  const params = new URLSearchParams({
    date: requestedDate.isoDate,
    domain: normalizedDomain,
  });

  onProgress?.({
    detail: `Preparing ${normalizedDomain} for ${requestedDate.label}.`,
    step: 'prepare',
  });

  let hadServiceResponse = false;

  try {
    onProgress?.({
      detail: 'Checking Wayback availability first.',
      endpoint: '/api/wayback/availability',
      step: 'availability',
    });

    const availabilityResult = await fetchWaybackStep('/api/wayback/availability', params, fetchImpl);
    hadServiceResponse = true;

    if (availabilityResult.snapshot) {
      onProgress?.({
        candidate: availabilityResult.snapshot.candidate,
        detail: `Found an available snapshot for ${availabilityResult.snapshot.candidate}.`,
        endpoint: '/api/wayback/availability',
        step: 'availability-hit',
      });

      onProgress?.({
        detail: 'Opening archive preview.',
        step: 'complete',
      });

      return availabilityResult;
    }
  } catch (error) {
    if (error instanceof InvalidArchiveDateError) {
      throw error;
    }
  }

  const candidates = buildDomainCandidates(normalizedDomain);
  const snapshots: WaybackSnapshot[] = [];

  for (const candidate of candidates) {
    const candidateSnapshots: WaybackSnapshot[] = [];

    for (const direction of ['before', 'after'] as const) {
      const cdxParams = new URLSearchParams(params);
      cdxParams.set('candidate', candidate);
      cdxParams.set('direction', direction);

      try {
        onProgress?.({
          candidate,
          detail: `Querying ${direction === 'before' ? 'captures before' : 'captures after'} the selected date for ${candidate}.`,
          endpoint: '/api/wayback/cdx-direction',
          step: direction === 'before' ? 'cdx-before' : 'cdx-after',
        });

        const cdxResult = await fetchCdxDirection(cdxParams, fetchImpl);
        hadServiceResponse = true;
        candidateSnapshots.push(...(cdxResult.snapshots || []));
      } catch (error) {
        if (error instanceof InvalidArchiveDateError) {
          throw error;
        }
      }
    }

    if (candidateSnapshots.length) {
      onProgress?.({
        candidate,
        detail: `Found CDX captures for ${candidate}.`,
        endpoint: '/api/wayback/cdx-direction',
        step: 'cdx-hit',
      });

      snapshots.push(...candidateSnapshots);
      break;
    }
  }

  if (!hadServiceResponse) {
    onProgress?.({
      candidate: candidates[0] || normalizedDomain,
      detail: 'Wayback metadata did not respond; building a direct replay URL.',
      step: 'best-effort',
    });

    return {
      alternates: [],
      candidates,
      requestedDate: requestedDate.isoDate,
      requestedDateLabel: requestedDate.label,
      searchedDomain: normalizedDomain,
      snapshot: buildBestEffortSnapshot(candidates[0] || normalizedDomain, requestedDate),
    };
  }

  const rankedSnapshots = rankSnapshots(dedupeSnapshots(snapshots), requestedDate, candidates);
  const snapshot = rankedSnapshots[0] || buildBestEffortSnapshot(candidates[0] || normalizedDomain, requestedDate);

  onProgress?.({
    candidate: snapshot.candidate,
    detail: rankedSnapshots[0] ? 'Opening the closest CDX capture.' : 'Opening a direct Wayback replay URL.',
    step: rankedSnapshots[0] ? 'complete' : 'best-effort',
  });

  return {
    alternates: rankedSnapshots.slice(1, 6),
    candidates,
    requestedDate: requestedDate.isoDate,
    requestedDateLabel: requestedDate.label,
    searchedDomain: normalizedDomain,
    snapshot,
  };
}

export async function findClosestSnapshotForDate(
  domain: string,
  dateParts: DateParts,
  fetchImpl: typeof fetch = fetch,
): Promise<WaybackSnapshot | null> {
  const result = await searchWaybackForDate(domain, dateParts, fetchImpl);
  return result.snapshot ?? null;
}
